import { formatCNPJ } from "@brazilian-utils/brazilian-utils";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Alert, AlertTitle, Box, Grid, ListItem, ListItemText, List as ListMui, Modal, Stack } from '@mui/material';
import { makeStyles, styled } from "@mui/styles";
import { uniqBy } from "lodash";
import moment from "moment";
import { useFormContext, useWatch } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import {
  BooleanField, BooleanInput, Button, Create, CreateButton, DateField, DateTimeInput, DeleteButton, Edit, EditButton, Filter, FormDataConsumer, FunctionField, ImageField, ImageInput,
  Labeled,
  List,
  NumberField, NumberInput, ReferenceArrayInput, ReferenceField, ReferenceInput,
  SaveButton,
  SelectArrayInput,
  SelectField,
  SelectInput, Show, SimpleForm, SimpleShowLayout, Tab, TabbedShowLayout, TextField, TextInput, Toolbar, TopToolbar,
  WrapperField,
  required,
  useDataProvider,
  useGetIdentity,
  useListContext, useNotify,
  useRecordContext, useRefresh
} from "react-admin";
import { ApproveFillinButton, CnpjField, CustomizableDatagrid, CustomizableImageField, DownloadFileInvoice, FillinReviewButton, FillinStatusField, ImportInvoiceButton, LazyLoadAutoCompleteInput, Permission, Permissions, PriceInput, ReprovedFillinButton, Tooltip } from '../components';
import CopyToChipboardField from "../components/CopyToChipboardField";
import { PERMISSIONS } from "../constants";
import { useRowClick } from "../hooks/useRowClick";
import { BusinessModel } from "../models/businessModel";
import { FillinReviewStatus, FillinStatus, PaymentMethodType } from "../models";
import { FillinPresetType } from "../models/fillinPresetType";
import { PaymentOrigin } from "../models/paymentOrigin";
import { UserRole } from "../providers/authProvider";
import { CNPJInput } from '../utils/Inputs';
import { formatCellphone, formatCpf, formatNumber, getChainId, getCompanyId, getStationId, linkDriveType } from "../utils/utils";
import ComponentWithPermissions from "./ComponentWithPermissions";
import { FillinLogsTab } from "./FillinLogsTab";

const editStyles = makeStyles({
  marginFilter: {
    marginLeft: '.25rem',
    marginBottom: '.5rem'
  },

  smallOdometer: {
    "& div": {
      width: 60,
      height: 100,
    },
    "& img": {
      width: "100%",
      height: "100%",
      cursor: "pointer",
    }
  },

  odometerModal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 310,
    height: 550,
    "& img": {
      width: "100%",
      height: "100%"
    },
  }
});

export const ScreenType = {
  billShow: 'billShow',
}

const ListActions = () => (
  <TopToolbar>
    <Permissions permissions={[PERMISSIONS.CREATE_FILLINS]} >
      <CreateButton label="Adicionar manualmente" />
    </Permissions>
  </TopToolbar>
);

const OdometerField = (props) => {
  const { identity } = useGetIdentity();
  const record = useRecordContext(props);

  return record ? (
    <WrapperField>
      <NumberField {...props} />
      {identity && identity.role === UserRole.chainCashier &&
        <Button
          label={""}
          startIcon={<ContentCopyIcon />}
          onClick={(event) => {
            event.stopPropagation();
            copyToClipboard(
              event.currentTarget.parentElement.firstChild
                .textContent
            );
          }}
        />}
    </WrapperField>
  ) : null;
};

const FillinFilters = (props) => {
  const classes = editStyles();
  const { identity } = useGetIdentity();

  return (
    <Filter {...props}>
      {
        Permissions({
          userRoles: [UserRole.station],
          children: (
            <BooleanInput
              className={classes.marginFilter}
              source="isFinished"
              label="Mostrar abastecimentos baixados"
              defaultValue={false}
            />
          )
        })
      }
      <SelectInput
        label="Status"
        source="status"
        choices={FillinStatus.fillinStatusChoices}
        emptyText="Todos"
        alwaysOn
      />
      {
        !getStationId() &&
        <ReferenceArrayInput
          label="Posto"
          reference="stations"
          source="stationIds"
          sort={{ field: 'name', order: 'ASC' }}
          perPage={false}
          filter={identity?.role === UserRole.company ? { companyId: getCompanyId() } : {}}
          alwaysOn
        >
          <SelectArrayInput optionText="name" label="Postos" />
        </ReferenceArrayInput>
      }
      {
        Permissions({
          userRoles: [UserRole.admin, UserRole.company],
          children: (
            <LazyLoadAutoCompleteInput
              label="Motorista"
              reference="drivers"
              source="driverId"
              format={choice => `${formatCpf(choice.cpf)} ${choice.register ? '- ' + choice.register : ''} - ${choice.name}`}
              style={{ minWidth: 260 }}
              size={"small"}
              alwaysOn
            />
          )
        })
      }
      {
        Permissions({
          userRoles: [UserRole.admin, UserRole.company],
          children: (
            <ReferenceInput source="chainId" reference="chains" textAlign="center" perPage={false} filter={identity?.role === UserRole.company ? { companyId: getCompanyId() } : {}} alwaysOn >
              <SelectInput optionText="name" label="Rede" emptyText="Todos" />
            </ReferenceInput>
          )
        })
      }
      {
        Permissions({
          userRoles: [UserRole.admin, UserRole.company],
          children: (
            <LazyLoadAutoCompleteInput
              label="Placa do Veículo"
              source="licensePlate"
              format={choice => choice.licensePlate}
              style={{ minWidth: 220 }}
              reference="vehicles"
              alwaysOn
            />
          )
        })
      }
    </Filter>
  )
}

const FillinList = (props) => {
  const { identity } = useGetIdentity();

  // let filterValue;
  // switch (identity?.role) {
  //   case UserRole.chain:
  //     filterValue = { chainId: getChainId() };
  //     break;
  //   case UserRole.company:
  //     filterValue = { companyId: getCompanyId() };
  //     break;
  //   case UserRole.chainCashier:
  //     filterValue = { chainId: getChainId(), stationId: getStationId() };
  //     break;
  //   default:
  //     filterValue = {}
  //     break;
  // }

  let defaultFilters = {};
  switch (identity?.role) {
    case UserRole.company:
      defaultFilters = { ...defaultFilters, status: FillinStatus.blocked };
      break;
    case UserRole.station:
      defaultFilters = { ...defaultFilters, isFinished: false };
      break;
  }

  return (
    <List {...props}
      sort={{ field: 'receivedAt', order: 'DESC' }}
      title="Abastecimentos"
      actions={<ListActions />}
      // filter={identity?.role === UserRole.chain ? { chainId: getChainId() } : identity?.role === UserRole.company ? { companyId: getCompanyId() } : identity?.role === UserRole.chainCashier ? { chainId: getChainId(), stationId: getStationId() } : {}}
      filters={<FillinFilters />}
      filterDefaultValues={defaultFilters}
      storeKey={null} // Disables storage, syncing only with the URL.
    >
      <FillinDatagrid rowClick="show" {...props} />
    </List>
  );
};

const InvoiceCnpjField = ({ label, ...props }) => {
  const record = useRecordContext();
  const { identity } = useGetIdentity();

  return (
    <CopyToChipboardField
      source="invoiceCnpj"
      label={label}
      showClipButton={identity && identity.role === UserRole.station && record.status === FillinStatus.approved}
      textAlign="center"
      Component={CnpjField}
      isHidden={record.status !== FillinStatus.approved || record.stationReviewStatus !== FillinReviewStatus.approved}
      {...props}
    />
  );
}

const ActionsStatus = () => {
  return (
    <>
      <ApproveFillinButton label="Aprovar" />
      <ReprovedFillinButton label="Reprovar" />
    </>
  )
}

export const FillinDatagrid = ({ disableBulkActionButtons = false, screen = '', ...props }) => {
  const [vehicleBases, setVehicleBases] = useState();
  const [station, setStation] = useState();
  const { handleRowClick } = useRowClick();

  const { data } = useListContext();
  const dataProvider = useDataProvider();
  const invoiceBaseIds = uniqBy(data?.map((fillin) => fillin.invoiceBaseId));
  const stationIds = uniqBy(data?.map((fillin) => fillin.stationId));
  const { identity } = useGetIdentity();

  const FinishFillinButton = () => {
    const dataProvider = useDataProvider();
    const record = useRecordContext();
    const refresh = useRefresh();
    const notify = useNotify();

    const doUpdate = (successMessage, errorMessage) => {
      dataProvider.update('fillins/finish', {
        id: record.id,
        previousData: record
      })
        .then(() => {
          notify(successMessage);
          refresh();
        })
        .catch(() => { notify(errorMessage, 'warning'); });
    }

    const successMessage = 'Abastecimento baixado com sucesso!';
    const errorMessage = 'Erro ao dar baixa no abastecimento.';

    return (
      !record.isFinished && record.status === FillinStatus.approved && record.stationReviewStatus === FillinReviewStatus.approved
        ? <Button
          sx={{ textWrap: 'nowrap' }}
          label="Dar baixa"
          onClick={(e) => {
            doUpdate(
              successMessage,
              errorMessage,
            )
            e.stopPropagation();
          }}
          variant="outlined"
        />
        : null
    );
  }

  const approvedRowStyle = (record) => ({
    backgroundColor: record.stationReviewStatus === FillinReviewStatus.pending ? '#FFF59D' : record.status === FillinStatus.blocked || record.stationReviewStatus === FillinReviewStatus.denied ? '#FFD1D9' : '#white',
    '& .MuiTableCell-root': {
      borderColor: 'rgb(190, 190, 190)',
      ...(record.stationReviewStatus === FillinReviewStatus.pending || record.status === FillinStatus.blocked ? { paddingTop: 2.5, paddingBottom: 2.5 } : {}),
    },
    '& .RaReferenceField-link > span, & .RaReferenceField-link > div, & .MuiButton-text': { color: '#3B9EFF' },
    // With green color for approved
    // backgroundColor : record.status === 'reproved' ? '#FFEBE6' : '#E3FDEE'
  });

  useEffect(() => {
    if (identity && identity.role === UserRole.station) {
      if (invoiceBaseIds?.length > 0) {
        dataProvider.getMany('vehicle-bases', {
          ids: invoiceBaseIds,
        }).then(({ data }) =>
          setVehicleBases(data.reduce((acc, curr) => ({ ...acc, [curr.id]: curr }), {}))
        );
      }
      if (stationIds?.length > 0) {
        dataProvider.getMany('stations', {
          ids: stationIds,
        }).then(({ data }) =>
          setStation(data.reduce((acc, curr) => ({ ...acc, [curr.id]: curr }), {}))
        );
      }
    }
  }, [JSON.stringify(invoiceBaseIds), JSON.stringify(stationIds)]);

  return (
    <CustomizableDatagrid
      {...props}
      rowClick={(...rest) => props.rowClick !== undefined ? handleRowClick(props.rowClick, ...rest) : identity && handleRowClick('show', ...rest)}
      resource="fillins"
      rowSx={approvedRowStyle}
      bulkActionButtons={false}
      disableColumnSorting={identity?.role === UserRole.station}
    >
      {
        props.bulkActions && props.bulkActions
      }
      {
        screen !== ScreenType.billShow &&
        <FillinStatusField label="Status" />
      }
      <SelectField emptyText="--" choices={PaymentMethodType.paymentMethodChoices} label="Forma de pagamento" source="paymentMethod" />
      {
        screen !== ScreenType.billShow &&
        Permissions({
          permissions: [PERMISSIONS.APPROVE_FILLINS],
          userRoles: [UserRole.company],
          children: (
            <ActionsStatus label="Controle" />
          )
        })
      }
      {
        screen !== ScreenType.billShow &&
        Permissions({
          userRoles: [UserRole.company, UserRole.admin],
          children: (
            <FunctionField
              label="Média do Abastecimento"
              render={(record) => {
                return record.lastOdometer ? formatNumber((record.odometer - record.lastOdometer) / record.amount, { maximumFractionDigits: 2, minimumFractionDigits: 2 }) : '-';
              }}
            />
          )
        })
      }
      {
        screen !== ScreenType.billShow &&
        Permissions({
          userRoles: [UserRole.company, UserRole.admin],
          children: (
            <FunctionField
              label="Média (Limites)"
              render={(record) => {
                return `${record.consumptionAverageBottomLimit || '-'}/${record.consumptionAverageTopLimit || '-'}`;
              }}
            />
          )
        })
      }
      {
        Permissions({
          userRoles: [UserRole.chain, UserRole.admin, UserRole.company],
          children: (
            <WrapperField label="Posto" textAlign="center">
              <ReferenceField
                reference="stations"
                source="stationId"
                textAlign="center"
                link={identity && [UserRole.admin, UserRole.chain].includes(identity.role) ? "show" : false}
              >
                <TextField source="name" />
              </ReferenceField>
              <FunctionField render={(record) => record && record.paymentOrigin ? ` - ${PaymentOrigin.translateOrigin(record.paymentOrigin)}` : ''} />
            </WrapperField>
          )
        })
      }
      <ReferenceField
        label="Combustível"
        reference="fuels"
        source="fuelId"
        textAlign="center"
        link={identity?.role === UserRole.admin ? "show" : false}
      >
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField
        label="Veículo"
        reference="vehicles"
        source="vehicleId"
        textAlign="center"
        link={identity && [UserRole.admin, UserRole.company].includes(identity.role) ? "show" : false}
      >
        <CopyToChipboardField source="licensePlate" />
      </ReferenceField>
      {
        Permissions({
          userRoles: [UserRole.admin, UserRole.chain, UserRole.station],
          children: (
            <NumberField source="hoseNumber" label="Bico" textAlign="center" emptyText='Não Informado' />
          )
        })
      }
      <NumberField source="amount" label="Quantidade em litros" locales="pt-BR" textAlign="center" />
      <NumberField source="value" label="Valor pago" locales="pt-BR" options={{ style: 'currency', currency: 'BRL' }} textAlign="center" />
      {
        screen !== ScreenType.billShow &&
        <CopyToChipboardField
          Component={NumberField}
          source="odometer"
          label="Odômetro"
          locales="pt-BR"
          textAlign="center"
          showClipButton={identity && identity.role === UserRole.station}
        />
      }
      <DateField source="receivedAt" label="Data inclusão" showTime locales="pt-BR" textAlign="center" />
      {
        screen !== ScreenType.billShow &&
        <BooleanField source="manuallyAdded" label="Manual?" />
      }
      {
        screen !== ScreenType.billShow &&
        <FunctionField source="deliveryId" label="Autorização" render={record => record.status === FillinStatus.approved && record.stationReviewStatus === FillinReviewStatus.approved ? record.numericId : "Indisponível"} />
      }
      {
        screen !== ScreenType.billShow &&
        Permissions({
          userRoles: [UserRole.admin, UserRole.chain, UserRole.station],
          children: (
            <InvoiceCnpjField label="CNPJ da nota" />
          )
        })
      }
      {
        screen !== ScreenType.billShow &&
        Permissions({
          permissions: [PERMISSIONS.VIEW_APPROVED_FILLINS_BY],
          children: (
            <ReferenceField
              label="Aprovado Por"
              emptyText="--"
              source="approvedBy"
              basePath="company-employees"
              reference="company-employees"
              link={identity && [UserRole.admin, UserRole.company].includes(identity.role) ? "edit" : false}
            >
              <TextField source="name" />
            </ReferenceField>
          )
        })
      }
      {
        Permissions({
          userRoles: [UserRole.admin, UserRole.chain],
          children: (
            <ReferenceField source="invoiceId" reference="invoices" emptyText="--" label="Nota Fiscal" textAlign="center" link={false}>
              <DownloadFileInvoice />
            </ReferenceField>
          )
        })
      }
      {
        (screen !== ScreenType.billShow && identity && [UserRole.admin, UserRole.company].includes(identity.role)) &&
        <DateField source="approvedAt" label="Aprovado em" emptyText="--" showTime locales="pt-BR" />
      }
      {
        Permissions({
          userRoles: [UserRole.station],
          children: (
            <FunctionField
              label="Aviso"
              render={(record) => {
                const vehicleBasesMustInvoice = vehicleBases ? vehicleBases[record.invoiceBaseId]?.mustIssueInvoice : null;
                const stationsMustInvoice = station ? station[record.stationId]?.mustIssueInvoice : null;

                if (vehicleBasesMustInvoice || stationsMustInvoice) {
                  return (
                    "A NFe deve ser emitida imediatamente"
                  );
                }
              }}
            />
          )
        })
      }
      {
        (
          screen !== ScreenType.billShow ||
          (screen !== ScreenType.billShow && identity && identity.role === UserRole.station) ||
          props.actions
        ) &&
        <Stack flexDirection="row" gap={2} label="Ações">
          {
            screen !== ScreenType.billShow &&
            Permissions({
              userRoles: [UserRole.station],
              children: (
                <FillinReviewButton source="stationReviewStatus" />
              )
            })
          }
          {
            (screen !== ScreenType.billShow && identity && identity.role === UserRole.station) &&
            <FinishFillinButton />
          }
          {
            props.actions && props.actions
          }
        </Stack>
      }
      {
        Permissions({
          userRoles: [UserRole.admin, UserRole.chain, UserRole.company],
          children: (
            <ReferenceField source="billId" reference="bills" emptyText="--" label="Fatura" textAlign="center" link={"show"}>
              <TextField source="numericId" />
            </ReferenceField>
          )
        })
      }
    </CustomizableDatagrid>
  );
};

const PresetValueField = ({ source }) => {
  const record = useRecordContext();

  return (
    record.presetType === FillinPresetType.cash ?
      <NumberField source={source} locales="pt-BR" options={{ style: 'currency', currency: 'BRL', minimumFractionDigits: 2 }} />
      :
      <NumberField source={source} locales="pt-BR" emptyText="--" />
  );
}

const FillinShow = props => {
  const { identity } = useGetIdentity();
  const [showOdometer, setShowOdometer] = useState(false);
  const classes = editStyles();

  const openModal = (evt) => {
    if (evt.target.tagName === "IMG") {
      setShowOdometer(true);
    }
  };

  const closeModal = () => {
    setShowOdometer(false);
  };

  const Messagefield = () => {
    const record = useRecordContext();
    const [vehicleBases, setVehicleBases] = useState({});
    const [station, setStation] = useState({});
    const dataProvider = useDataProvider();

    useEffect(() => {
      if (record.invoiceBaseId) {
        dataProvider.getMany('vehicle-bases', { ids: [record.invoiceBaseId] }).then(({ data }) => {
          setVehicleBases(data[0]);
        });
      }
      if (record.stationId) {
        dataProvider.getMany('stations', { ids: [record.stationId] }).then(({ data }) => {
          setStation(data[0]);
        });
      }
    }, [dataProvider, record.invoiceBaseId, record.stationId]);

    if (vehicleBases.mustIssueInvoice || station.mustIssueInvoice) {
      return (
        "A NFe deve ser emitida imediatamente"
      );
    }
  };

  const FillinInfo = () => {
    const record = useRecordContext();
    return (
      <SimpleShowLayout>
        <Grid container width={{ xs: '100%', xl: 1200 }} spacing={2}>
          <Grid item xs={12} md={6}>
            <Box component="fieldset" sx={{ border: '1px dashed grey' }}>
              <Legend>Detalhes do abastecimento</Legend>
              <SimpleShowLayout style={{ padding: 0 }}>
                {
                  Permission({
                    userRole: UserRole.admin,
                    children: (
                      <SelectField
                        source="businessModel"
                        label="Modelo de Negócio"
                        choices={BusinessModel.businessModelChoices}
                        sx={{ fontWeight: 'bold', fontSize: '1.1em' }}
                      />
                    )
                  })
                }
                <TextField source="id" label="ID" />
                {
                  Permission({
                    userRole: UserRole.admin,
                    children: (
                      <TextField source="reserveId" label="ID da reserva CTT" />
                    )
                  })
                }
                <ReferenceField
                  label="Combustível"
                  reference="fuels"
                  source="fuelId"
                  link={identity?.role === UserRole.admin}
                >
                  <TextField source="name" />
                </ReferenceField>
                <ReferenceField source="contractId" reference="contracts" label="Código do contrato" textAlign="center" emptyText="--" link={identity?.role === UserRole.admin ? "show" : false}>
                  <TextField source="code" />
                </ReferenceField>
                <NumberField source="amount" label="Quantidade em litros" locales="pt-BR" />
                {
                  Permissions({
                    userRoles: [UserRole.company, UserRole.admin],
                    children: (
                      <FunctionField
                        label="Média do Abastecimento"
                        render={(record) => {
                          return record.lastOdometer ? formatNumber((record.odometer - record.lastOdometer) / record.amount, { maximumFractionDigits: 2, minimumFractionDigits: 2 }) : '-';
                        }}
                      />
                    )
                  })
                }
                {!record?.manuallyAdded && (
                  <>
                    {record?.businessModel === BusinessModel.pumpxPro ? (
                      <Labeled label="Preço por litro - Bomba" fullWidth>
                        <FunctionField
                          render={(rec) => {
                            return rec.oldPricePerLiter && rec.oldPricePerLiter > 0
                              ? rec.oldPricePerLiter.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 3 })
                              : '--';
                          }}
                        />
                      </Labeled>
                    ) : record?.businessModel === BusinessModel.pumpxPlus ? (
                      <>
                        <Labeled label="Preço por litro - Portal" fullWidth>
                          <FunctionField
                            render={(rec) => {
                              return rec.oldPricePerLiter && rec.oldPricePerLiter > 0
                                ? rec.oldPricePerLiter.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 3 })
                                : '--';
                            }}
                          />
                        </Labeled>
                        <Labeled label="Preço por litro - Bomba" fullWidth>
                          <FunctionField
                            render={(rec) => {
                              return rec.pumpPricePerLiter && rec.pumpPricePerLiter > 0
                                ? rec.pumpPricePerLiter.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 3 })
                                : '--';
                            }}
                          />
                        </Labeled>
                      </>
                    ) : (
                      <Labeled label="Preço por litro - Bomba" fullWidth>
                        <FunctionField
                          render={(rec) => {
                            return rec.oldPricePerLiter && rec.oldPricePerLiter > 0
                              ? rec.oldPricePerLiter.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 3 })
                              : '--';
                          }}
                        />
                      </Labeled>
                    )}
                  </>
                )}
                <Labeled label="Preço por litro - Acordo" fullWidth>
                  <FunctionField
                    render={(rec) => {
                      return rec.pricePerLiter && rec.pricePerLiter > 0
                        ? rec.pricePerLiter.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 3 })
                        : '--';
                    }}
                  />
                </Labeled>
                <Labeled label="Valor pago" fullWidth>
                  <FunctionField
                    render={(rec) => {
                      return rec.value && rec.value > 0
                        ? rec.value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                        : '--';
                    }}
                  />
                </Labeled>
                <NumberField source="hoseNumber" label="Bico" emptyText="--" />
                <NumberField source="encerrante" label="Encerrante" locales="pt-BR" emptyText="--" />
                <BooleanField source="fillingCompletely" label="Encheu o tanque?" />
                <BooleanField source="manuallyAdded" label="Abastecimento manual?" />
                <ReferenceField source="invoiceId" label="Nota Fiscal" reference="invoices" emptyText="--" link={false}>
                  <DownloadFileInvoice />
                </ReferenceField>
                <SelectField emptyText="--" choices={PaymentMethodType.paymentMethodChoices} label="Forma de pagamento" source="paymentMethod" />
                <DateField source="receivedAt" label="Data inclusão" showTime locales="pt-BR" />
                {
                  Permissions({
                    userRoles: [UserRole.admin, UserRole.chain, UserRole.station],
                    children: (
                      <InvoiceCnpjField label="CNPJ da nota" emptyText='--' />
                    )
                  })
                }
                <FillinStatusField label="Status" />
                <TextField label="Aprovado por" source="approvedBy" emptyText="--" />
                {
                  Permissions({
                    userRoles: [UserRole.admin, UserRole.chain, UserRole.company],
                    children: (
                      <ReferenceField source="billId" reference="bills" emptyText="--" label="Fatura" textAlign="center" link={"show"}>
                        <TextField source="numericId" />
                      </ReferenceField>
                    )
                  })
                }
              </SimpleShowLayout>
            </Box>
            <Box component="fieldset" sx={{ border: '1px dashed grey', marginTop: 2 }}>
              <Legend>Preset selecionado</Legend>
              <SimpleShowLayout style={{ padding: 0 }}>
                <ReferenceField
                  label="Combustível"
                  reference="fuels"
                  source="fuelId"
                  link={identity?.role === UserRole.admin}
                >
                  <TextField source="name" />
                </ReferenceField>
                <SelectField source="presetType" label="Limite" choices={FillinPresetType.fillinPresetTypeChoices} />
                {
                  record?.presetType !== FillinPresetType.completely ?
                    <PresetValueField source="presetValue" label="Valor" />
                    :
                    <></>
                }
              </SimpleShowLayout>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box component="fieldset" sx={{ border: '1px dashed grey' }}>
              <Legend>Detalhes do veículo</Legend>
              <SimpleShowLayout style={{ padding: 0 }}>
                <ReferenceField
                  source="subCompanyId"
                  label="Transportadora"
                  reference="sub-companies"
                  emptyText="Não definida"
                  link={identity && [UserRole.admin, UserRole.company].includes(identity.role) ? 'show' : null}
                >
                  <TextField source="name" />
                </ReferenceField>
                <ReferenceField
                  source="subCompanyId"
                  label="CNPJ"
                  reference="sub-companies"
                  emptyText="Não definido"
                  link={identity && [UserRole.admin, UserRole.company].includes(identity.role) ? 'show' : null}
                >
                  <FunctionField render={record => record && formatCNPJ(record.cnpj)} />
                </ReferenceField>
                {
                  identity && identity.role === UserRole.station && (
                    <ReferenceField
                      source="subCompanyId"
                      label="Telefone da Transportadora"
                      reference="sub-companies"
                      emptyText="Não definido"
                      link={identity && [UserRole.admin, UserRole.company].includes(identity.role) ? 'show' : null}
                    >
                      <FunctionField render={record => formatCellphone(record.phone)} />
                    </ReferenceField>
                  )
                }
                {
                  Permissions({
                    permissions: [PERMISSIONS.LIST_DRIVERS],
                    children: (
                      <ReferenceField
                        label="Motorista"
                        reference="drivers"
                        source="driverId"
                        link={identity && [UserRole.admin, UserRole.company].includes(identity.role) ? linkDriveType : false}
                      >
                        <TextField source="name" />
                      </ReferenceField>
                    )
                  })
                }
                {identity && [UserRole.admin, UserRole.company].includes(identity.role) &&
                  <ReferenceField
                    label="Manobrista"
                    reference="drivers"
                    source="valetId"
                    emptyText="Sem manobrista"
                    link={identity && [UserRole.admin, UserRole.company].includes(identity.role) ? linkDriveType : false}
                  >
                    <TextField source="name" />
                  </ReferenceField>
                }
                <ReferenceField
                  label="Placa"
                  reference="vehicles"
                  source="vehicleId"
                  link={(identity && [UserRole.admin, UserRole.company].includes(identity.role)) ? 'show' : false}
                >
                  <CopyToChipboardField source="licensePlate" />
                </ReferenceField>
                <Box component="fieldset" sx={{ border: '1px dashed grey', marginTop: 2 }}>
                  <Legend>Odômetros</Legend>
                  <SimpleShowLayout style={{ padding: 0 }}>
                    <NumberField source="odometer" label="Odômetro Digitado" locales="pt-BR" />
                    <NumberField source="odometerTrucksControl" label="Odômetro da Telemetria" locales="pt-BR" emptyText="-" />
                    <NumberField source="dateInicioTrucksControl" label="Data da Telemetria" locales="pt-BR" emptyText="-" />
                    <NumberField source="lastOdometer" label="Odômetro Anterior" locales="pt-BR" emptyText="-" />
                  </SimpleShowLayout>
                </Box>
              </SimpleShowLayout>
            </Box>
            <Box component="fieldset" sx={{ border: '1px dashed grey', marginTop: 2 }}>
              <Legend>Local</Legend>
              <SimpleShowLayout style={{ padding: 0 }}>
                {identity && [UserRole.admin, UserRole.company].includes(identity.role) &&
                  <ReferenceField
                    label="Rede"
                    source="chainId"
                    emptyText="Não identificada"
                    reference="chains"
                    link={identity?.role === UserRole.admin}
                  >
                    <TextField source="name" />
                  </ReferenceField>
                }
                <ReferenceField
                  label="Posto"
                  reference="stations"
                  source="stationId"
                  link={(identity && [UserRole.admin, UserRole.chain].includes(identity.role)) ? "show" : false}
                >
                  <TextField source="name" />
                </ReferenceField>
                <ReferenceField
                  label="Frentista"
                  reference="station-attendants"
                  source="attendantId"
                  emptyText="Não encontrado"
                >
                  <TextField source="name" />
                </ReferenceField>
                <ReferenceField
                  label="Adicionado por"
                  source="createdBy"
                  reference="employees"
                  emptyText="--"
                >
                  <TextField source="name" />
                </ReferenceField>
              </SimpleShowLayout>
            </Box>
            <Box component="fieldset" sx={{ border: '1px dashed grey', marginTop: 2 }}>
              <Legend>Faturas</Legend>
              <SimpleShowLayout style={{ padding: 0 }}>
                {
                  Permissions({
                    userRoles: [UserRole.admin, UserRole.chain],
                    children: (
                      <ReferenceField
                        label="Fatura a Receber (Posto)"
                        source="stationBillingId"
                        reference="station-billings"
                        emptyText="--"
                        link="show"
                      >
                        <TextField source="numericId" />
                      </ReferenceField>
                    )
                  })
                }
                {
                  Permissions({
                    userRoles: [UserRole.admin, UserRole.company],
                    children: (
                      <ReferenceField
                        label="Fatura a Pagar (Transportadora)"
                        source="subCompanyBillingId"
                        reference="sub-company-billings"
                        emptyText="--"
                        link="show"
                      >
                        <TextField source="numericId" />
                      </ReferenceField>
                    )
                  })
                }
              </SimpleShowLayout>
            </Box>
            <Box component="fieldset" sx={{ border: '1px dashed grey', marginTop: 2 }}>
              <Legend>Imagens</Legend>
              <SimpleShowLayout style={{ padding: 0 }}>
                <CustomizableImageField
                  source="odometerPath"
                  label="Imagem Odômetro (Clique para ampliar)"
                  emptyText='Nenhuma imagem encontrada.'
                  onClick={(evt) => openModal(evt)}
                  className={classes.smallOdometer}
                />
                <Modal
                  open={showOdometer}
                  onClose={closeModal}
                  aria-labelledby="Bigger Odometer Modal"
                  aria-describedby="A modal that shows a bigger photo of the odometer."
                >
                  <CustomizableImageField
                    source="odometerPath"
                    label="Imagem Odômetro"
                    className={classes.odometerModal}
                  />
                </Modal>
                <ImageField
                  source="invoicePath"
                  label="Imagem Nota Fiscal"
                  emptyText='Nenhuma imagem encontrada.'
                />
              </SimpleShowLayout>
            </Box>
            {record && (!record.generateFinancialMovement || record.status === FillinStatus.reproved || record.status === FillinStatus.blocked || [FillinReviewStatus.pending, FillinReviewStatus.denied].includes(record.stationReviewStatus))
              &&
              <Alert variant="outlined" severity="info" sx={{ marginTop: 2 }}>
                <AlertTitle>Não será possível importar uma nota para esse abastecimento pelo(s) motivo(s):</AlertTitle>
                <ListMui>
                  {
                    !record.generateFinancialMovement &&
                    <ListItem disablePadding>
                      <ListItemText
                        primary="Não gera transação financeira"
                      />
                    </ListItem>
                  }
                  {
                    record.status === FillinStatus.blocked &&
                    <ListItem disablePadding>
                      <ListItemText
                        primary="Bloqueado pelo sistema"
                      />
                    </ListItem>
                  }
                  {
                    record.status === FillinStatus.reproved &&
                    <ListItem disablePadding>
                      <ListItemText
                        primary="Reprovado pelo sistema"
                      />
                    </ListItem>
                  }
                  {
                    [FillinReviewStatus.pending, FillinReviewStatus.denied].includes(record.stationReviewStatus) &&
                    <ListItem disablePadding>
                      <ListItemText
                        primary="Não aprovado pelo caixa"
                      />
                    </ListItem>
                  }
                </ListMui>
              </Alert>
            }
            {
              identity?.role === UserRole.station &&
              <Box component="fieldset" sx={{ border: '1px dashed grey', marginTop: 2 }}>
                <Legend>Avisos</Legend>
                <Messagefield />
              </Box>
            }
          </Grid>
        </Grid>
      </SimpleShowLayout>
    );
  }

  const ShowActions = (props) => (
    <TopToolbar {...props}>
      <Permission permission={PERMISSIONS.CREATE_INVOICE}>
        <ImportInvoiceButton />
      </Permission>
      <Permission permission={PERMISSIONS.CREATE_FILLINS}>
        <EditButton />
      </Permission>
    </TopToolbar>
  );

  return (
    <Show
      {...props}
      title="Abastecimento"
      actions={<ShowActions />}
    >
      <TabbedShowLayout>
        <Tab label="Resumo">
          <FillinInfo />
        </Tab>
        <Permission permission={PERMISSIONS.ADMIN}>
          <Tab label="Logs" path="logs">
            <FillinLogsTab />
          </Tab>
        </Permission>
      </TabbedShowLayout>
    </Show>
  );
};

const FuelInput = () => {
    const { resetField } = useFormContext();
  const stationId = useWatch({ name: 'stationId' });
  const subCompanyId = useWatch({ name: 'subCompanyId' });
  const licensePlate = useWatch({ name: 'licensePlate' });
  const cnpj = useWatch({ name: 'cnpj' });
  const corporateName = useWatch({ name: 'corporateName' });
  const filter = {};

  const isDisabled = useMemo(() => {
    return !((stationId || (corporateName && cnpj)) && licensePlate && subCompanyId);
  }, [stationId, corporateName, cnpj, licensePlate, subCompanyId]);

  if (stationId) filter.stationId = stationId;
  if (licensePlate) filter.licensePlate = licensePlate;
  if (subCompanyId) filter.subCompanyId = subCompanyId;

  useEffect(() => {
    resetField("fuelId");
  }, [stationId, corporateName, cnpj, licensePlate, subCompanyId]);

  return (
    <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Exibe apenas os combustíveis permitidos para o veículo e previstos no contrato">
      <ReferenceInput
        reference="fuels"
        source="fuelId"
        sort={{ field: "name", order: "ASC" }}
        queryOptions={{ enabled: !isDisabled }}
        filter={filter}
      >
        <SelectInput label="Combustível" optionText="name" validate={required()} disabled={isDisabled} isRequired fullWidth />
      </ReferenceInput>
    </Tooltip>
  );
};

// Validações baseadas no schema Joi do backend
const validateLicensePlate = (value) => {
  if (!value) return undefined;
  if (value.length > 7) {
    return 'A placa deve ter no máximo 7 caracteres';
  }
  return undefined;
};

const validateCNPJ = (value) => {
  if (!value) return undefined;
  const cnpjNumbers = value.replace(/\D/g, '');
  if (cnpjNumbers.length !== 14) {
    return 'O CNPJ deve ter 14 dígitos';
  }
  return undefined;
};

const validateCorporateName = (value) => {
  if (!value) return undefined;
  if (value.length > 125) {
    return 'A razão social deve ter no máximo 125 caracteres';
  }
  return undefined;
};

const validatePositiveNumber = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  if (value <= 0) {
    return 'O valor deve ser positivo';
  }
  return undefined;
};

const validateAmount = [required(), validatePositiveNumber];
const validateValue = [required(), validatePositiveNumber];
const validatePricePerLiter = [required(), validatePositiveNumber];
const validateOldPricePerLiter = [required(), validatePositiveNumber];

const FillinCreate = props => {
  const { identity } = useGetIdentity();

  return (
    <Create
      {...props}
      title='Cadastrar abastecimento'
      transform={data => {
        const { cnpj, corporateName, ...rest } = data;
        if (data.stationId) { return rest; }
        return data;
      }}
    >
      <SimpleForm sanitizeEmptyValues defaultValues={{ employeeId: identity && identity.role === UserRole.admin ? null : localStorage.getItem('id') }}>
        <Grid container>
          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <Grid item xs={12} md={6}>
              <ReferenceInput
                label="Transportadora"
                source="subCompanyId"
                reference="sub-companies"
                filter={identity && identity.role === UserRole.company ? { companyId: getCompanyId() } : {}}
                sort={{ field: "name", order: "ASC" }}
                validate={required()}
                perPage={false}
              >
                <SelectInput label="Transportadora" optionText="name" fullWidth />
              </ReferenceInput>
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectInput
                label="Pagamento"
                choices={PaymentOrigin.paymentOriginChoices}
                source="paymentOrigin"
                fullWidth
              />
            </Grid>
          </Grid>

          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <Grid item xs={12} md={6}>
              <ReferenceInput
                reference="drivers"
                source="valetId"
                filter={{ isValet: true, ...(identity?.role === UserRole.company ? { companyId: getCompanyId() } : {}) }}
                sort={{ field: 'name', order: 'ASC' }}
                emptyText="Selecione um manobrista"
                pagination={null}
                perPage={null}
                allowEmpty
              >
                <SelectInput label="Manobrista" optionText="name" fullWidth />
              </ReferenceInput>
            </Grid>
            <Grid item xs={12} md={6}>
              <LazyLoadAutoCompleteInput
                label="Motorista"
                reference="drivers"
                source="driverId"
                format={choice => `${formatCpf(choice.cpf)} ${choice.register ? '- ' + choice.register : ''} - ${choice.name}`}
                style={{ minWidth: 400 }}
                size={"small"}
                fullWidth
              />
            </Grid>
          </Grid>

          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <Grid item xs={12} md={4}>
              <ReferenceInput
                source="stationId"
                reference={identity?.role === UserRole.company ? "companies/companyId/stations" : "stations"}
                filter={identity?.role === UserRole.chain ? { chainId: getChainId() } : {}}
                sort={{ field: "name", order: "ASC" }}
                perPage={false}
              >
                <SelectInput label="Posto" optionText="name" defaultValue={identity?.role === UserRole.station ? getStationId() : undefined} disabled={identity?.role === UserRole.station} fullWidth />
              </ReferenceInput>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormDataConsumer>
                {({ formData }) => (
                  !formData.stationId ? <CNPJInput source="cnpj" label="CNPJ" disabled={!!formData.stationId} validate={validateCNPJ} fullWidth /> : null
                )}
              </FormDataConsumer>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormDataConsumer>
                {({ formData }) => (
                  !formData.stationId ? <TextInput source="corporateName" label="Razão Social" disabled={!!formData.stationId} validate={validateCorporateName} fullWidth /> : null
                )}
              </FormDataConsumer>
            </Grid>
          </Grid>

          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <Grid item xs={12} md={4}>
              <LazyLoadAutoCompleteInput
                label="Placa do Veículo"
                source="licensePlate"
                format={choice => choice.licensePlate}
                style={{ minWidth: 160 }}
                reference="vehicles"
                validate={validateLicensePlate}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <NumberInput source="odometer" label="Odômetro" locales="pt-BR" validate={required()} fullWidth />
            </Grid>
            <Grid item xs={12} md={4}>
              <NumberInput source="lastOdometer" label="Odômetro Anterior" locales="pt-BR" fullWidth />
            </Grid>
          </Grid>

          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <Grid item xs={12} md={4}>
              <FuelInput />
            </Grid>
            <Grid item xs={12} md={4}>
              <PriceInput source="pricePerLiter" label="Preço por litro - Acordo" precision="3" locales="pt-BR" validate={validatePricePerLiter} fullWidth />
            </Grid>
            <Grid item xs={12} md={4}>
              <PriceInput source="oldPricePerLiter" label="Preço por litro - Bomba" precision="3" locales="pt-BR" validate={validateOldPricePerLiter} fullWidth />
            </Grid>
          </Grid>

          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <Grid item xs={12} md={3}>
              <NumberInput source="amount" label="Quantidade em litros" locales="pt-BR" validate={validateAmount} fullWidth />
            </Grid>
            <Grid item xs={12} md={3}>
              <PriceInput source="value" label="Valor total" locales="pt-BR" validate={validateValue} fullWidth />
            </Grid>
            <Grid item xs={12} md={3}>
              <DateTimeInput source="createdAt" label="Data do concentrador" locales="pt-BR" parse={(value) => moment(value).toISOString()} validate={required()} fullWidth />
            </Grid>
            <Grid item xs={12} md={3}>
              <DateTimeInput source="receivedAt" label="Data de recebimento" locales="pt-BR" parse={(value) => moment(value).toISOString()} validate={required()} fullWidth />
            </Grid>
          </Grid>

          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <Grid item xs={12} md={12}>
              <TextInput source="reasonForAddition" label="Justificativa" validate={required()} fullWidth />
            </Grid>
          </Grid>
        </Grid>

        <BooleanInput source="fillingCompletely" label="Encheu o tanque?" />
        <BooleanInput source="generateFinancialMovement" label="Gerar movimentação financeira?" defaultValue={false} />
      </SimpleForm>
    </Create>
  );
};

const ToolBarCustom = () => (
  <Toolbar style={{ justifyContent: 'space-between' }}>
    <SaveButton />
    <Permission permission={PERMISSIONS.ADMIN} userRole={UserRole.admin}>
      <DeleteButton />
    </Permission>
  </Toolbar>
);

const FillinEdit = props => {
  const { identity } = useGetIdentity();

  const disabled = !identity || identity?.role !== UserRole.admin;

  return (
    <Edit title='Editar abastecimento' {...props}
      transform={data => {
        delete data.pumpPrices;
        return data;
      }}>
      <SimpleForm toolbar={<ToolBarCustom />}>
        <Grid container>
          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <Grid item xs={12} md={6}>
              <ReferenceInput
                label="Transportadora"
                source="subCompanyId"
                reference="sub-companies"
                filter={{ companyId: getCompanyId() }}
                sort={{ field: "name", order: "ASC" }}
                validate={required()}>
                <SelectInput disabled label="Transportadora" optionText="name" fullWidth />
              </ReferenceInput>
            </Grid>
            <Grid item xs={12} md={6}>
              <CNPJInput source="invoiceCnpj" label="CNPJ da Nota" disabled={disabled} fullWidth />
            </Grid>
          </Grid>

          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <Grid item xs={12} md={6}>
              <ReferenceInput
                label="Manobrista"
                reference="drivers"
                source="valetId"
                filter={{ isValet: true }}
                sort={{ field: 'name', order: 'ASC' }}
                emptyText="Selecione um manobrista"
                allowEmpty
              >
                <SelectInput disabled label="Manobrista" optionText="name" fullWidth />
              </ReferenceInput>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormDataConsumer>
                {({ formData }) => (
                  <LazyLoadAutoCompleteInput
                    label="Motorista"
                    reference="drivers"
                    source="driverId"
                    format={choice => `${formatCpf(choice.cpf)} ${choice.register ? '- ' + choice.register : ''} - ${choice.name}`}
                    size={"small"}
                    disabled
                    record={formData}
                    fullWidth
                  />
                )}
              </FormDataConsumer>
            </Grid>
          </Grid>

          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <Grid item xs={12} md={6}>
              <FormDataConsumer>
                {({ formData }) => (
                  <LazyLoadAutoCompleteInput
                    label="Veículo"
                    source="vehicleId"
                    disabled
                    format={choice => choice.licensePlate}
                    reference="vehicles"
                    record={formData}
                    fullWidth />
                )}
              </FormDataConsumer>
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberInput source="odometer" label="Odômetro" locales="pt-BR" fullWidth />
            </Grid>
          </Grid>

          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <Grid item xs={12} md={3}>
              <ReferenceInput
                source="fuelId"
                reference="fuels"
                sort={{ field: 'name', order: 'ASC' }}
                validate={required()}
              >
                <SelectInput disabled label="Combustível" optionText="name" fullWidth />
              </ReferenceInput>
            </Grid>
            <Grid item xs={12} md={3}>
              <NumberInput source="hoseNumber" label="Bico" locales="pt-BR" fullWidth />
            </Grid>
            <Grid item xs={12} md={3}>
              <NumberInput disabled={disabled} source="amount" label="Quantidade em litros" locales="pt-BR" validate={required()} fullWidth />
            </Grid>
            <Grid item xs={12} md={3}>
              <PriceInput disabled={disabled} source="value" label="Valor pago" locales="pt-BR" validate={required()} fullWidth />
            </Grid>
          </Grid>

          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <Grid item xs={12} md={4}>
              <PriceInput precision="3" disabled={disabled} source="oldPricePerLiter" label="Preço por litro - Bomba" locales="pt-BR" fullWidth />
            </Grid>
            <Grid item xs={12} md={4}>
              <PriceInput precision="3" disabled={disabled} source="pricePerLiter" label="Preço por litro - Acordo" locales="pt-BR" validate={required()} fullWidth />
            </Grid>
            <Grid item xs={12} md={4}>
              <NumberInput source="encerrante" label="Encerrante" locales="pt-BR" fullWidth />
            </Grid>
          </Grid>

          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <Grid item xs={12} md={6}>
              <DateTimeInput
                source="createdAt"
                label="Data de recebimento"
                locales="pt-BR"
                parse={(value) => moment(value).toISOString()}
                validate={required()}
                disabled={disabled}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DateTimeInput
                source="receivedAt"
                label="Data do concentrador"
                locales="pt-BR"
                parse={(value) => moment(value).toISOString()}
                validate={required()}
                disabled={disabled}
                fullWidth
              />
            </Grid>
          </Grid>

          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <Grid item xs={12} md={12}>
              <TextInput disabled source="reasonForAddition" label="Justificativa" fullWidth />
            </Grid>
          </Grid>

          {
            Permission({
              permission: PERMISSIONS.ADMIN,
              userRole: UserRole.admin,
              children: (
                <Grid container xs={12} md={10} xl={8} spacing={2}>
                  <Grid item xs={12} md={6}>
                    <SelectInput
                      source="status"
                      label="Status (Sistema)"
                      choices={FillinStatus.fillinStatusChoices}
                      sx={{ minWidth: 220 }}
                      isRequired
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <SelectInput
                      source="stationReviewStatus"
                      label="Status (Caixa)"
                      choices={FillinReviewStatus.fillinReviewStatusChoices}
                      sx={{ minWidth: 220 }}
                      isRequired
                      fullWidth
                    />
                  </Grid>
                </Grid>
              )
            })
          }

          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <Grid item xs={12} md={12}>
              <BooleanInput disabled={disabled} source="generateFinancialMovement" label="Gerar movimentação financeira?" defaultValue={false} />
              <BooleanInput source="fillingCompletely" label="Encheu o tanque?" />
            </Grid>
          </Grid>

          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <Grid item xs={12} md={6}>
              <ImageInput disabled source="odometerFile" label="Imagem Odômetro" accept="image/*" fullWidth>
                <ImageField source="src" label="Imagem" />
              </ImageInput>
            </Grid>
            <Grid item xs={12} md={6}>
              <ImageInput disabled source="invoiceFile" label="Imagem Nota Fiscal" accept="image/*" fullWidth>
                <ImageField source="src" label="Imagem" />
              </ImageInput>
            </Grid>
          </Grid>

        </Grid>
        <Permissions userRoles={[UserRole.company]} permissions={[PERMISSIONS.APPROVE_FILLINS]}>
          <Labeled label="Ações" style={{ marginTop: 10 }}>
            <ApproveFillinButton style={{ marginBottom: 10, marginTop: 10 }} label="Aprovar abastecimento" />
          </Labeled>
        </Permissions>
      </SimpleForm>
    </Edit>
  );
};

const Legend = styled('legend')({
  paddingLeft: 12,
  paddingRight: 12,
});

const resource = {
  list: FillinList,
  edit: new ComponentWithPermissions({ component: FillinEdit, permissions: [PERMISSIONS.CREATE_FILLINS] }),
  create: new ComponentWithPermissions({ component: FillinCreate, permissions: [PERMISSIONS.CREATE_FILLINS] }),
  show: FillinShow,
  name: 'fillins',
  permissions: [PERMISSIONS.LIST_FILLINS, PERMISSIONS.APPROVE_FILLINS]
};

export default resource;
